import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthFetch } from '../AuthComponent';
import { useStore } from '../StoreContext';

// Number of rows to accumulate in the initial buffer (and each subsequent loadMore)
// Reduced from 500 → 200: each loadMore now fetches 4 cursor pages instead of
// 10. Batch completes in ~800ms instead of ~2s, so the user sees new rows
// land before they reach the end of the buffer. Threshold lowered from 0.8 to
// 0.6 so prefetch fires earlier — by the time scroll reaches the bottom of
// the current buffer, the next 200 rows are already in.
const VIRTUAL_BATCH_TARGET = 200;
// Backend page size (hardcoded server-side at 50)
const BACKEND_PAGE_SIZE = 50;
// Number of backend pages to chain per virtual batch
const PAGES_PER_BATCH = Math.ceil(VIRTUAL_BATCH_TARGET / BACKEND_PAGE_SIZE); // 10

// Fetch next batch when user has scrolled past this fraction of loaded rows
export const PREFETCH_THRESHOLD = 0.6;

/**
 * useVirtualOrdersQuery — like useOrdersQuery but:
 *  - Initial load chains up to 10 cursor pages (500 rows) before displaying
 *  - Buffer is accumulated (not replaced) so virtualizer never resets mid-scroll
 *  - loadMore() chains another 500 rows when called (invoked by VirtualTbody
 *    when user scrolls past PREFETCH_THRESHOLD)
 *
 * Returns:
 *   orders, fetching, error, totalCount, hasMore,
 *   loadMore, refresh, filterOptions, updateOrderTags, PREFETCH_THRESHOLD
 */
export default function useVirtualOrdersQuery({
  statuses,
  clubs,
  teams,
  dateFrom,
  dateTo,
  printeds,
  pickStatuses,
  pressStatuses,
  packStatuses,
  deliveryMethods, deliveryNames,
  pickupStores,
  storeStatuses,
  isCustoms,
  crms,
  sources,
  retailStore,        // Retail Unfulfilled chip filter (Eric, 2026-10-08)
  locations,
  sku = '',
  skuUnpicked = false,
  skuExclude = false,
  splitTeamFulfillment = false,
  includeArchived = false,
  sortKey = 'created_at',
  sortDir = 'desc',
  enabled = true,
  emulatingSloc = null,
  urlLoc = null,
}) {
  const storeFetch = useAuthFetch();
  const { activeStore } = useStore(); // eslint-disable-line

  const [orders, setOrders]         = useState([]);
  const [fetching, setFetching]     = useState(false);
  const [error, setError]           = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [hasMore, setHasMore]       = useState(false);
  const [filterOptions, setFilterOptions] = useState({ clubs: [], teams_by_club: {}, pickup_stores: [], crms: [] });

  // DB keyset cursor for the start of the NEXT virtual batch
  const nextBatchCursorRef = useRef(null);
  // Increment on every filter/sort change to discard stale responses
  const fetchVersionRef    = useRef(0);
  // Guard: prevent concurrent loadMore calls
  const loadingMoreRef     = useRef(false);

  // Build URLSearchParams from current filter state (no page_size — backend ignores it)
  const buildParams = useCallback((dbCursor) => {
    const p = new URLSearchParams();
    (statuses || []).forEach(s => p.append('statuses', s));
    (clubs || []).forEach(c => p.append('clubs', c));
    (teams || []).forEach(t => p.append('teams', t));
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo)   p.set('date_to', dateTo);
    (printeds || []).forEach(v => p.append('printeds', v));
    (pickStatuses || []).forEach(v => p.append('pick_statuses', v));
    (pressStatuses || []).forEach(v => p.append('press_statuses', v));
    (packStatuses || []).forEach(v => p.append('pack_statuses', v));
    (deliveryMethods || []).forEach(v => p.append('delivery_methods', v));
    (deliveryNames || []).forEach(v => p.append('delivery_names', v));
    (pickupStores || []).forEach(s => p.append('pickup_stores', s));
    (storeStatuses || []).forEach(v => p.append('store_statuses', v));
    (isCustoms || []).forEach(v => p.append('is_customs', v));
    (crms || []).forEach(v => p.append('crms', v));
    (sources || []).forEach(v => p.append('sources', v));
    if (retailStore) p.append('retail_store', '1');
    (locations || []).forEach(v => p.append('locations', v));
    if ((sku || '').trim()) {
      p.set('sku', sku.trim());
      if (skuUnpicked) p.set('sku_unpicked', '1');
      if (skuExclude) p.set('sku_exclude', '1');
    }
    if (splitTeamFulfillment) p.set('split_team_fulfillment', '1');
    if (includeArchived) p.set('include_archived', '1');
    if (urlLoc) p.set('loc', urlLoc);
    p.set('sort_key', sortKey);
    p.set('sort_dir', sortDir);
    if (dbCursor) p.set('db_cursor', dbCursor);
    return p;
  }, [statuses, clubs, teams, dateFrom, dateTo, printeds, pickStatuses, pressStatuses,
      packStatuses, deliveryMethods, deliveryNames, pickupStores, storeStatuses, isCustoms, crms,
      sources, retailStore, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived, sortKey, sortDir, urlLoc]);

  /**
   * Chains up to maxPages cursor fetches starting from startCursor.
   * Returns { rows: FO[], nextCursor: string|null }.
   * Stops early if a page returns no next_cursor (end of data).
   * Aborts immediately if version has changed.
   */
  const fetchBatch = useCallback(async (startCursor, maxPages, version) => {
    const rows = [];
    let cursor = startCursor;
    for (let i = 0; i < maxPages; i++) {
      if (version !== fetchVersionRef.current) return null; // stale
      const params = buildParams(cursor);
      const res = await storeFetch(`/api/orders?${params}`);
      if (version !== fetchVersionRef.current) return null;
      if (!res.ok) throw new Error('Failed to load orders');
      const data = await res.json();
      if (version !== fetchVersionRef.current) return null;
      const page = data.orders || [];
      rows.push(...page);
      if (data.total != null && i === 0) {
        // capture total on first page
        setTotalCount(data.total);
      }
      cursor = data.next_cursor ?? null;
      if (!cursor) break; // no more data
    }
    return { rows, nextCursor: cursor };
  }, [buildParams, storeFetch]);

  const fetchCount = useCallback(async () => {
    const version = fetchVersionRef.current;
    try {
      const params = buildParams(null);
      const res = await storeFetch(`/api/orders/count?${params}`);
      if (res.ok) {
        const d = await res.json();
        if (Math.abs(fetchVersionRef.current - version) <= 1) setTotalCount(d.count);
      }
    } catch {}
  }, [buildParams, storeFetch]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo)   p.set('date_to', dateTo);
      (crms || []).forEach(v => p.append('crms', v));
      const res = await storeFetch(`/api/orders/filter-options?${p}`);
      if (res.ok) {
        const d = await res.json();
        setFilterOptions(prev => ({ ...prev, ...d }));
      }
    } catch {}
  }, [dateFrom, dateTo, crms, storeFetch]);

  // Initial fetch: reset buffer, load first VIRTUAL_BATCH_TARGET rows
  const fetchInitial = useCallback(async () => {
    fetchVersionRef.current += 1;
    const version = fetchVersionRef.current;
    nextBatchCursorRef.current = null;
    loadingMoreRef.current = false;
    setOrders([]);
    setHasMore(false);
    setError(null);
    setFetching(true);
    try {
      const result = await fetchBatch(null, PAGES_PER_BATCH, version);
      if (!result) return; // stale/aborted
      setOrders(result.rows);
      nextBatchCursorRef.current = result.nextCursor;
      setHasMore(result.nextCursor !== null);
    } catch (e) {
      if (version === fetchVersionRef.current) setError(e.message);
    } finally {
      if (version === fetchVersionRef.current) setFetching(false);
    }
  }, [fetchBatch]);

  // loadMore: append the next batch of rows
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    if (!nextBatchCursorRef.current) return;
    loadingMoreRef.current = true;
    const version = fetchVersionRef.current;
    const cursor  = nextBatchCursorRef.current;
    try {
      const result = await fetchBatch(cursor, PAGES_PER_BATCH, version);
      if (!result) { loadingMoreRef.current = false; return; }
      setOrders(prev => [...prev, ...result.rows]);
      nextBatchCursorRef.current = result.nextCursor;
      setHasMore(result.nextCursor !== null);
    } catch {}
    loadingMoreRef.current = false;
  }, [fetchBatch]);

  // Reset + refetch when any filter/sort changes. Stable filterKey + `enabled`
  // gate (matches useOrdersQuery) — both keep this hook inert when virtual
  // mode isn't active, so it doesn't fire on /orders-v2 (cursor mode).
  const filterKey = JSON.stringify({
    statuses, clubs, teams, dateFrom, dateTo, printeds,
    pickStatuses, pressStatuses, packStatuses,
    deliveryMethods, deliveryNames, pickupStores, storeStatuses,
    isCustoms, crms, sources, retailStore, locations, sku, skuUnpicked, skuExclude, splitTeamFulfillment, includeArchived,
    sortKey, sortDir,
    emulatingSloc,
    urlLoc,
  });
  useEffect(() => {
    if (!enabled) return;
    setTotalCount(null);
    fetchInitial();
    fetchCount();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, enabled]);

  const refresh = useCallback(() => {
    setTotalCount(null);
    fetchInitial();
    fetchCount();
  }, [fetchInitial, fetchCount]);

  const updateOrderTags = useCallback((orderId, tags) => {
    setOrders(prev => prev.map(fo =>
      fo.order?.id === orderId
        ? { ...fo, order: { ...fo.order, tags } }
        : fo
    ));
  }, []);

  return {
    orders,
    fetching,
    error,
    totalCount,
    hasMore,
    loadMore,
    refresh,
    filterOptions,
    updateOrderTags,
    PREFETCH_THRESHOLD,
  };
}
